<template>
  <div style="position: relative;">
    <!-- Party Details -->
    <PartyDetailsCard
      class="mb-4"
      :item="peParty"
    />

    <div v-if="peSurveysValidationActive">
      <PartyDetailsCard
        v-for="item of partySurveyCardsByIdentifiers('party-details-card')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <!-- Identifiers -->
    <IdentifiersCard
      :id="id"
      :item="peParty"
      class="mb-4"
    />

    <div v-if="peSurveysValidationActive">
      <IdentifiersCard
        v-for="item of partySurveyCardsByIdentifiers('identifiers-card')"
        :id="id"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <!-- Addicional Identifiers -->
    <div>
      <AdditionalIdentifiersCard
        v-for="ident of peParty.identifiers"
        :key="ident.id"
        :item="ident"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <AdditionalIdentifiersCard
        v-for="item of partySurveyCardsByIdentifiers('additional-identifiers-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Identifier"
      :has-new-section="peAdditionalIdentifiersCardHasNewSection"
      @on-add="peAdditionalIdentifiersCardAddSection"
    />

    <!-- Demographics -->
    <DemographicsCard
      :id="id"
      :item="peParty"
      class="mb-4"
    />

    <div v-if="peSurveysValidationActive">
      <DemographicsCard
        v-for="item of partySurveyCardsByIdentifiers('demographics-card')"
        :id="id"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <PhysicalAddressCard
      class="mb-4"
      :item="peParty.physical_address"
    />

    <div v-if="peSurveysValidationActive">
      <PhysicalAddressCard
        v-for="item of partySurveyCardsByIdentifiers('physical-address-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <MailingAddressCard
      class="mb-4"
      :item="peParty.mailing_address"
    />

    <div v-if="peSurveysValidationActive">
      <MailingAddressCard
        v-for="item of partySurveyCardsByIdentifiers('mailing-address-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <RelativeSectionAdderLine
      title="Address"
      property="addresses"
      @on-add="onImportAddresses"
    />

    <!-- Email Cards -->
    <div>
      <EmailCard
        v-for="(email, index) of peParty.emails"
        :key="index"
        :item="email"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <EmailCard
        v-for="item of partySurveyCardsByIdentifiers('email-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Email"
      :has-new-section="peEmailCardHasNewSection"
      @on-add="peEmailCardAddSection"
    />

    <RelativeSectionAdderLine
      title="Email"
      property="emails"
      @on-add="peEmailMultipleCardCreate"
    />

    <!-- Phone Cards -->
    <div>
      <PhoneCard
        v-for="(phone, index) of peParty.phones"
        :key="index"
        :item="phone"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <PhoneCard
        v-for="item of partySurveyCardsByIdentifiers('phone-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Phone"
      :has-new-section="pePhoneCardHasNewSection"
      @on-add="pePhoneCardAddSection"
    />

    <RelativeSectionAdderLine
      title="Phone"
      property="phones"
      @on-add="pePhoneMultipleCardCreate"
    />

    <!-- AKA Cards -->
    <div>
      <AKACard
        v-for="aka of peParty.akas"
        :key="aka.id"
        :item="aka"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <AKACard
        v-for="item of partySurveyCardsByIdentifiers('aka-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="AKA"
      :has-new-section="peAKACardHasNewSection"
      @on-add="peAKACardAddSection"
    />

    <!-- Passport Cards -->
    <div>
      <PassportCard
        v-for="passport of peSortedCharacteristic('passports')"
        :key="passport.id"
        :item="passport"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <PassportCard
        v-for="item of partySurveyCardsByIdentifiers('passport-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Passport"
      :has-new-section="pePassportCardHasNewSection"
      @on-add="pePassportCardAddSection"
    />

    <!-- ImmigrationStatus Cards -->
    <div>
      <ImmigrationStatusCard
        v-for="immigration_status of currentImmigrationStatuses"
        :key="immigration_status.id"
        :item="immigration_status"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ImmigrationStatusCard
        v-for="item of partySurveyCardsByIdentifiers('immigration-status-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Immigration Status"
      :has-new-section="peImmigrationStatusCardHasNewSection"
      @on-add="peImmigrationStatusCardAddSection"
    />

    <!-- Narratives Cards -->
    <div>
      <NarrativesCard
        v-for="narr of peSortedCharacteristic('narratives')"
        :key="narr.id"
        :item="narr"
        class="mb-4"
      />
    </div>

    <SectionAdderLine
      section="Narrative"
      :has-new-section="peNarrativesCardHasNewSection"
      @on-add="peNarrativesCardAddSection"
    />

    <v-overlay
      absolute
      :value="!canView"
      :class="{'party-overlay': !canView}"
      class="align-start"
    >
      <v-row
        align="start"
        justify="center"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <h2>You do not have permissions to view this section.</h2>
        </v-col>
      </v-row>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Actions from '@/data/Permissions/enums.json'
import { PARTY_EDIT_SECTIONS } from '@/router/party-edit'
import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { USER_NAMESPACE } from '@/store/user'

import AKACard from './SectionCards/PersonalDataCards/AKACard.vue'
import AdditionalIdentifiersCard from './SectionCards/PersonalDataCards/AdditionalIdentifiersCard.vue'
import DemographicsCard from './SectionCards/PersonalDataCards/DemographicsCard.vue'
import EmailCard from './SectionCards/PersonalDataCards/EmailCard.vue'
import IdentifiersCard from './SectionCards/PersonalDataCards/IdentifiersCard.vue'
import ImmigrationStatusCard from './SectionCards/PersonalDataCards/ImmigrationStatusCard.vue'
import MailingAddressCard from './SectionCards/PersonalDataCards/MailingAddressCard.vue'
import NarrativesCard from './SectionCards/PersonalDataCards/NarrativesCard.vue'
import PartyDetailsCard from './SectionCards/PersonalDataCards/PartyDetailsCard.vue'
import PassportCard from './SectionCards/PersonalDataCards/PassportCard.vue'
import PhoneCard from './SectionCards/PersonalDataCards/PhoneCard.vue'
import PhysicalAddressCard from './SectionCards/PersonalDataCards/PhysicalAddressCard.vue'
import RelativeSectionAdderLine from './SectionTools/RelativeSectionAdderLine.vue'
import SectionAdderLine from './SectionTools/SectionAdderLine.vue'

export default {
  name: 'PEPersonalDetails',

  components: {
    AKACard,
    AdditionalIdentifiersCard,
    DemographicsCard,
    EmailCard,
    IdentifiersCard,
    ImmigrationStatusCard,
    MailingAddressCard,
    NarrativesCard,
    PartyDetailsCard,
    PassportCard,
    PhoneCard,
    PhysicalAddressCard,
    RelativeSectionAdderLine,
    SectionAdderLine,
  },

  beforeRouteLeave (_, __, next) {
    if (this.peAnyUnlockedSections) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.peLockPartySections()
        next()
      } else next(false)
    } else {
      this.peLockPartySections()
      next()
    }
  },

  props: {
    id: {
      type: String,
      required: true,
    },

    quickEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      canView: true,
    }
  },

  computed: {
    currentImmigrationStatuses () {
      return this.peParty.immigration_statuses || []
    },

    userCan () {
      return {
        viewSection: this.can(Actions.Platform.PartyEdit.Sections.PersonalDetails.View),
      }
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAKACardHasNewSection',
      'peAdditionalIdentifiersCardHasNewSection',
      'peAnyUnlockedSections',
      'peEmailCardHasNewSection',
      'peImmigrationStatusCardHasNewSection',
      'peNarrativesCardHasNewSection',
      'peParty',
      'pePassportCardHasNewSection',
      'pePhoneCardHasNewSection',
      'peSortedCharacteristic',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'partySurveyCardsByIdentifiers',
      'peSurveysValidationActive',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  created () {
    if (!this.userCan.viewSection) {
      this.canView = false
      this.displayAlert('error', 'You do not have permissions to view Party Personal Details.')
      this.moveToNextPermittedSection()
      return
    }

    this.peGetSelectedPartySection({ id: this.id, section: 'personal_details' }).
      catch(e => {
        if (e.status === 403) this.$router.push({ name: 'resourceAccessDenied', query: { party_id: this.id } })
        else if (e.status === 404) this.$router.push({ name: 'resourceNotFound' })
        else this.displayAlert('error', `An unexpected error occurred: ${e.message}`)
      })
  },

  methods: {
    moveToNextPermittedSection () {
      const sections = PARTY_EDIT_SECTIONS

      for (const section of sections) {
        if (this.can(section.action)) {
          if (this.quickEdit) this.$emit('section-redirect', section.action)
          else this.$router.push({ name: `pe-${section.route}`, params: { id: this.id } })

          break
        }
      }
    },

    onImportAddresses (addresses) {
      const mailingAddressId = this.peParty.mailing_address.id
      const physicalAddressId = this.peParty.physical_address.id

      for (const address of addresses) {
        if (address.address_type === 'mailing') {
          this.peMailingAddressCardUpdate({ id: mailingAddressId, section: address })
        } else {
          this.pePhysicalAddressCardUpdate({ id: physicalAddressId, section: address })
        }
      }
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peAKACardAddSection',
      'peAdditionalIdentifiersCardAddSection',
      'peEmailCardAddSection',
      'peEmailMultipleCardCreate',
      'peGetSelectedPartySection',
      'peImmigrationStatusCardAddSection',
      'peLockPartySections',
      'peMailingAddressCardUpdate',
      'peNarrativesCardAddSection',
      'pePassportCardAddSection',
      'pePhoneCardAddSection',
      'pePhoneMultipleCardCreate',
      'pePhysicalAddressCardUpdate',
    ]),
  },
}
</script>

<style scoped>
.party-overlay {
  margin-top: -75px;
  padding-top: 50vh;
  z-index: 10;
  backdrop-filter: blur(6px);
}
</style>
